<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import BulkSendsAPI from 'dashboard/api/bulkSends';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BulkSendDialog from 'dashboard/components-next/BulkSend/BulkSendDialog.vue';

const { t } = useI18n();
const route = useRoute();

const bulkSends = ref([]);
const isFetching = ref(true);
const openId = ref(null);
const recipients = ref([]);
const dialogRef = ref(null);

const fetchBulkSends = async () => {
  isFetching.value = true;
  try {
    const { data } = await BulkSendsAPI.get();
    bulkSends.value = data.payload;
  } finally {
    isFetching.value = false;
  }
};

const toggle = async id => {
  if (openId.value === id) {
    openId.value = null;
    return;
  }
  openId.value = id;
  recipients.value = [];
  const { data } = await BulkSendsAPI.show(id);
  recipients.value = data.recipients;
};

const conversationLink = id =>
  frontendURL(conversationUrl({ accountId: route.params.accountId, id }));

// A recipient that was handed to WhatsApp is described by what WhatsApp did
// with it; one that never got that far by why not.
const recipientState = recipient =>
  recipient.status === 'sent' && recipient.delivery_status
    ? recipient.delivery_status
    : recipient.status;

onMounted(fetchBulkSends);
</script>

<template>
  <CampaignLayout
    :header-title="t('BULK_SEND.PAGE.TITLE')"
    :button-label="t('BULK_SEND.PAGE.NEW')"
    @click="dialogRef.open()"
  >
    <BulkSendDialog ref="dialogRef" @sent="fetchBulkSends" />
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <p v-else-if="!bulkSends.length" class="py-10 text-sm text-n-slate-11">
      {{ t('BULK_SEND.PAGE.EMPTY') }}
    </p>
    <div v-else class="flex flex-col gap-2 pb-8">
      <div
        v-for="bulkSend in bulkSends"
        :key="bulkSend.id"
        class="border rounded-xl border-n-weak"
      >
        <button
          type="button"
          class="flex flex-wrap items-center justify-between w-full gap-3 p-4 text-start"
          @click="toggle(bulkSend.id)"
        >
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ bulkSend.template_name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ bulkSend.inbox_name }} · {{ bulkSend.sender_name }} ·
              {{ messageStamp(bulkSend.created_at, 'LLL d, h:mm a') }}
            </span>
            <span
              v-if="bulkSend.next_send_at > Date.now() / 1000"
              class="text-xs text-n-amber-11"
            >
              {{
                t('BULK_SEND.PAGE.SCHEDULED_FOR', {
                  time: messageStamp(bulkSend.next_send_at, 'LLL d, h:mm a'),
                })
              }}
            </span>
          </div>
          <div class="flex flex-wrap gap-3 text-xs text-n-slate-11">
            <span
              >{{ t('BULK_SEND.STATUS.pending') }}:
              {{ bulkSend.counts.pending }}</span
            >
            <span
              >{{ t('BULK_SEND.STATUS.sent') }}:
              {{ bulkSend.counts.sent }}</span
            >
            <span class="text-n-ruby-11">
              {{ t('BULK_SEND.PAGE.NOT_DELIVERED') }}:
              {{ bulkSend.counts.failed + bulkSend.counts.delivery_failed }}
            </span>
            <span
              >{{ t('BULK_SEND.STATUS.skipped') }}:
              {{ bulkSend.counts.skipped }}</span
            >
          </div>
        </button>
        <div
          v-if="openId === bulkSend.id"
          class="px-4 pb-4 overflow-x-auto border-t border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-start text-n-slate-11">
                <th class="py-2 font-medium text-start">
                  {{ t('BULK_SEND.PAGE.CONTACT') }}
                </th>
                <th class="py-2 font-medium text-start">
                  {{ t('BULK_SEND.PAGE.STATE') }}
                </th>
                <th class="py-2 font-medium text-start">
                  {{ t('BULK_SEND.PAGE.DETAIL') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="recipient in recipients"
                :key="recipient.contact_id"
                class="border-t border-n-weak"
              >
                <td class="py-2 text-n-slate-12">
                  {{ recipient.contact_name || recipient.phone_number }}
                  <span class="text-xs text-n-slate-11">
                    {{ recipient.phone_number }}
                  </span>
                </td>
                <td class="py-2 text-n-slate-12">
                  {{ t(`BULK_SEND.STATUS.${recipientState(recipient)}`) }}
                </td>
                <td class="py-2 text-xs text-n-slate-11">
                  <router-link
                    v-if="recipient.conversation_id"
                    :to="conversationLink(recipient.conversation_id)"
                    class="text-n-blue-11"
                  >
                    #{{ recipient.conversation_id }}
                  </router-link>
                  {{ recipient.delivery_error || recipient.error }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
